import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

export type ToastSeverity = "error" | "success" | "info";

export interface Toast {
  id: number;
  message: string;
  severity: ToastSeverity;
}

interface ToastContextValue {
  toasts: Toast[];
  /** Show a toast; returns its id. */
  show: (message: string, severity?: ToastSeverity, durationMs?: number) => number;
  error: (message: string, durationMs?: number) => number;
  success: (message: string, durationMs?: number) => number;
  info: (message: string, durationMs?: number) => number;
  dismiss: (id: number) => void;
}

const noop = () => 0;

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  show: noop,
  error: noop,
  success: noop,
  info: noop,
  dismiss: () => {},
});

const DEFAULT_DURATION = 4000;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);
  const timers = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (
      message: string,
      severity: ToastSeverity = "info",
      durationMs: number = DEFAULT_DURATION
    ) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, message, severity }]);
      const timer = window.setTimeout(() => dismiss(id), durationMs);
      timers.current.set(id, timer);
      return id;
    },
    [dismiss]
  );

  const error = useCallback(
    (message: string, durationMs?: number) => show(message, "error", durationMs),
    [show]
  );
  const success = useCallback(
    (message: string, durationMs?: number) => show(message, "success", durationMs),
    [show]
  );
  const info = useCallback(
    (message: string, durationMs?: number) => show(message, "info", durationMs),
    [show]
  );

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, show, error, success, info, dismiss }),
    [toasts, show, error, success, info, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => useContext(ToastContext);

const ToastContainer: React.FC<{
  toasts: Toast[];
  onDismiss: (id: number) => void;
}> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;
  return (
    <div className="wade-toast-container" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`wade-toast wade-toast--${t.severity}`}
          onClick={() => onDismiss(t.id)}
        >
          <span className="wade-toast__message">{t.message}</span>
          <button
            type="button"
            className="wade-toast__close"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(t.id);
            }}
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
};
